import { useEffect, useState } from 'react';
import { formatClock } from '../lib/roundClock';
import type { LiveRoundResponse } from '../types';
import type { StrategistActionKey } from '../hooks/useStrategistActions';
import styles from './MatchControlsBar.module.css';

export type MatchControlsBarProps = {
  round: LiveRoundResponse | null;
  clockSecs: number | null;
  nextStrategyLabel: string | null;
  startingSide: string | null;
  currentSide: string | null;
  pendingAction: StrategistActionKey | null;
  actionError: string | null;
  onStart: () => void;
  onWin: () => void;
  onLoss: () => void;
  onReset: () => void;
  onSetClock: (secs: number) => void;
  onPlant: () => void;
  onDefuse: () => void;
  onNextRound: () => void;
  onEndGame: () => void;
  onSetStartingSide: (side: 'T' | 'CT') => void;
};

export default function MatchControlsBar({
  round,
  clockSecs,
  nextStrategyLabel,
  startingSide,
  currentSide,
  pendingAction,
  actionError,
  onStart,
  onWin,
  onLoss,
  onReset,
  onSetClock,
  onPlant,
  onDefuse,
  onNextRound,
  onEndGame,
  onSetStartingSide,
}: MatchControlsBarProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const status = (round?.status ?? '—').toLowerCase();
  const busy = pendingAction != null;
  const startNorm = (startingSide ?? '').toUpperCase();
  const curNorm = (currentSide ?? '').toUpperCase();
  const canStart = status === 'pending' || status === 'buy';
  const canEnd = status === 'buy' || status === 'active';
  const canClock = status === 'buy' || status === 'active';
  const canNextRound = status !== 'buy' && status !== 'active';
  const planted = Boolean(round?.bombPlantedAt) && !round?.bombDefusedAt;
  const canPlant = status === 'active' && !round?.bombPlantedAt;
  const canDefuse = status === 'active' && planted;

  const clockLabel = clockSecs == null ? '—' : formatClock(clockSecs);

  useEffect(() => {
    setConfirmEnd(false);
  }, [status, pendingAction]);

  return (
    <section className={`${styles.bar} ${collapsed ? styles.collapsed : ''}`} aria-label="Match controls">
      <button
        type="button"
        className={styles.head}
        aria-expanded={!collapsed}
        onClick={() => setCollapsed((c) => !c)}
      >
        <div className={styles.left}>
          <svg className={styles.chev} viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 9l6 6 6-6" />
          </svg>
          <span className={styles.title}>Match controls</span>
          <div className={styles.chips}>
            <span className={`${styles.chip} ${styles.chipActive}`}>{status || '—'}</span>
            <span className={`${styles.chip} ${styles.chipClock}`}>{clockLabel}</span>
            {nextStrategyLabel && (
              <span className={`${styles.chip} ${styles.chipMuted}`}>Next · {nextStrategyLabel}</span>
            )}
            {planted && (
              <span className={`${styles.chip} ${styles.chipPlanted}`}>
                Planted{clockSecs != null ? ` · ${clockLabel}` : ''}
              </span>
            )}
            {round?.bombDefusedAt && <span className={styles.chip}>Defused</span>}
            {curNorm && (
              <span className={styles.chip} title="Current side (from starting side + half rules)">
                Side · {curNorm}
              </span>
            )}
            {startNorm && (
              <span className={`${styles.chip} ${styles.chipMuted}`} title="Starting side (regulation first half)">
                Start · {startNorm}
              </span>
            )}
          </div>
        </div>
      </button>

      <div className={styles.body}>
        <div className={styles.group}>
          <div className={styles.lbl}>Starting side</div>
          <div className={styles.row}>
            <button
              type="button"
              className={`${styles.btn} ${startNorm === 'T' ? styles.primary : ''}`}
              disabled={busy}
              title="We start regulation on T"
              onClick={() => onSetStartingSide('T')}
            >
              Start T
            </button>
            <button
              type="button"
              className={`${styles.btn} ${startNorm === 'CT' ? styles.primary : ''}`}
              disabled={busy}
              title="We start regulation on CT"
              onClick={() => onSetStartingSide('CT')}
            >
              Start CT
            </button>
          </div>
        </div>

        <div className={styles.group}>
          <div className={styles.lbl}>Round</div>
          <div className={styles.row}>
            <button
              type="button"
              className={`${styles.btn} ${styles.primary}`}
              disabled={!canStart || busy}
              title={canStart ? 'Start live play' : 'Round is not pending/buy'}
              onClick={onStart}
            >
              Start
            </button>
            <button type="button" className={`${styles.btn} ${styles.win}`} disabled={!canEnd || busy} onClick={onWin}>
              Win
            </button>
            <button type="button" className={`${styles.btn} ${styles.loss}`} disabled={!canEnd || busy} onClick={onLoss}>
              Loss
            </button>
          </div>
        </div>

        <div className={styles.group}>
          <div className={styles.lbl}>Match</div>
          <div className={styles.row}>
            <button
              type="button"
              className={styles.btn}
              disabled={!canNextRound || busy}
              title={
                canNextRound
                  ? 'Create or open the next pending round'
                  : 'Finish the current round first'
              }
              onClick={onNextRound}
            >
              Next round
            </button>
            {confirmEnd ? (
              <>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.danger}`}
                  disabled={busy}
                  onClick={() => {
                    setConfirmEnd(false);
                    onEndGame();
                  }}
                >
                  Confirm end?
                </button>
                <button
                  type="button"
                  className={styles.btn}
                  disabled={busy}
                  onClick={() => setConfirmEnd(false)}
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                className={`${styles.btn} ${styles.danger}`}
                disabled={busy}
                onClick={() => setConfirmEnd(true)}
              >
                End game
              </button>
            )}
          </div>
        </div>

        <div className={styles.group}>
          <div className={styles.lbl}>Reset</div>
          <div className={styles.row}>
            <button
              type="button"
              className={styles.btn}
              disabled={!canClock || busy}
              title="Reset phase clock to default duration"
              onClick={onReset}
            >
              Reset
            </button>
          </div>
        </div>

        <div className={styles.group}>
          <div className={styles.lbl}>Game clock</div>
          <div className={styles.row}>
            <span className={styles.clockReadout}>{clockLabel}</span>
            {[115, 60, 40, 10].map((secs) => (
              <button
                key={secs}
                type="button"
                className={`${styles.btn} ${styles.preset}`}
                disabled={!canClock || busy}
                onClick={() => onSetClock(secs)}
              >
                {formatClock(secs)}
              </button>
            ))}
            <button
              type="button"
              className={`${styles.btn} ${styles.preset}`}
              disabled={!canClock || busy || clockSecs == null}
              onClick={() => clockSecs != null && onSetClock(Math.max(1, clockSecs - 10))}
            >
              −10
            </button>
            <button
              type="button"
              className={`${styles.btn} ${styles.preset}`}
              disabled={!canClock || busy || clockSecs == null}
              onClick={() => clockSecs != null && onSetClock(clockSecs + 10)}
            >
              +10
            </button>
          </div>
        </div>

        <div className={styles.group}>
          <div className={styles.lbl}>Bomb</div>
          <div className={styles.row}>
            <button
              type="button"
              className={`${styles.btn} ${styles.plant}`}
              disabled={!canPlant || busy}
              onClick={onPlant}
            >
              Plant
            </button>
            <button
              type="button"
              className={`${styles.btn} ${styles.defuse}`}
              disabled={!canDefuse || busy}
              onClick={onDefuse}
            >
              Defuse
            </button>
          </div>
        </div>

        {actionError && <p className={styles.error}>{actionError}</p>}
      </div>
    </section>
  );
}
